// POC Discovery Questions — based on FrankieOne Confluence POC guide + OKX France real-world example

export const DISCOVERY_SECTIONS = [
  {
    id: 'define',
    title: 'Define the POC',
    description: 'Outline what the POC is looking to solve',
    questions: [
      { id: 'problem', label: 'What problem is the customer hoping to solve?', placeholder: 'e.g., Increase pass rates in France from 55% to 65%, validate FrankieOne vs current vendor...' },
      { id: 'check_types_detail', label: 'What is the customer looking to test (KYC, KYB, KYT)?', placeholder: 'KYC & AML for France. For KYC follow Frankie Batch Template. For KYB provide ACN/ABNs in Google Sheet. For KYT data runs via Postman through Sardine.' },
      { id: 'results_format', label: 'How does the customer wish to see results?', placeholder: 'JSON, Portal, Presentation/pitch deck' },
      { id: 'timeline', label: 'What is the expected timeline?', placeholder: 'e.g., 2 weeks, end of quarter...' },
    ],
  },
  {
    id: 'success',
    title: 'Success Criteria',
    description: 'Establish what success looks like',
    questions: [
      { id: 'pass_rate_increase', label: 'Is the customer interested in seeing an increase in pass rates?', placeholder: 'Yes — current France match rate is 55%, target is 65%. 65% is required to create a strong business case for engineering to approve a switch.' },
      { id: 'system_interaction', label: 'Interested in seeing how FrankieOne interacts with their current system?', placeholder: '' },
      { id: 'performance', label: 'Interested in performance / response times?', placeholder: '' },
      { id: 'vendor_resilience', label: 'Interested in vendor resilience (what happens when a vendor goes down)?', placeholder: '' },
      { id: 'back_book', label: 'Is this a back-book remediation (checking historical customers)?', placeholder: 'Customer moving from old system to FrankieOne, checking historical records for compliance.' },
    ],
  },
  {
    id: 'scope',
    title: 'Size & Scope',
    description: 'Establish the data and coverage requirements',
    questions: [
      { id: 'record_count', label: 'How many records are they looking to test?', placeholder: 'e.g., 500 records' },
      { id: 'check_types_scope', label: 'What types of checks? (KYC, AML, KYB, KYT)', placeholder: 'KYC + AML' },
      { id: 'countries', label: 'What countries?', placeholder: 'France only for this POC' },
      { id: 'connectors', label: 'What connectors/vendors do they want to test?', placeholder: 'Socure and GDC. 4 data sources have been approved so far.' },
      { id: 'consent', label: 'Has the customer collected consent from the entities being tested?', placeholder: 'Required for consent-based sources (Experian, Equifax, DVS). Confirm status.' },
      { id: 'gov_id', label: 'Does the customer have Government ID data for the entities?', placeholder: 'Required for Gov ID sources. If not available, 34+ sources across 7+ countries are excluded.' },
    ],
  },
  {
    id: 'matching',
    title: 'Match Criteria',
    description: 'Must be confirmed with Vanessa Fierens and signed off by customer before POC runs',
    questions: [
      { id: 'match_description', label: 'Matching criteria description', placeholder: 'e.g., Matching is name and address (partial). Street name + postal code required. Street name only needs one word match.' },
      { id: 'vanessa_confirmed', label: 'Has Vanessa Fierens confirmed the match criteria?', placeholder: 'Yes/No — must be confirmed before POC proceeds' },
    ],
  },
  {
    id: 'data_transfer',
    title: 'Data Transfer',
    description: 'How customer data will be transferred to FrankieOne',
    questions: [
      { id: 'transfer_method', label: 'Data transfer method', placeholder: 'SFTP or Keybase (secure file transfer app)' },
      { id: 'template_sent', label: 'Has the POC/Batch template been sent to the customer?', placeholder: 'Customer must fill out the Frankie Batch Template correctly — incorrect data leads to bad results.' },
      { id: 'file_received', label: 'Has the data file been received?', placeholder: 'e.g., Mazen to send 500 records via email.' },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing',
    description: 'Do not commit to any pricing — these projects are non-standard',
    questions: [
      { id: 'pricing_model', label: 'Requested pricing model', placeholder: 'e.g., Data source based pricing, Cost per success pricing (positioned first in cascade)' },
      { id: 'pricing_notes', label: 'Pricing notes', placeholder: 'Do not commit to pricing. Can the POC be accomplished via standard commercial agreements?' },
    ],
  },
  {
    id: 'internal',
    title: 'Internal Alignment',
    description: 'Internal steps to set up the POC',
    questions: [
      { id: 'hubspot_ticket', label: 'HubSpot ticket created?', placeholder: 'Create ticket with Technical Section > Implementation Support Required > POC / Complex Demo. Link: https://frankieone.atlassian.net/browse/FRANKIE-XXXXX' },
      { id: 'slack_channel', label: 'Slack channel created?', placeholder: 'Channel with CS, Tech Ops, Solution Support. Name: #poc-{customer}' },
      { id: 'cs_involved', label: 'Customer Success involved? (for new customer connector creds)', placeholder: 'If new customer, CS handles forms and credentials (~10 business days turnaround)' },
      { id: 'contact_name', label: 'Customer contact name & role', placeholder: 'e.g., Mazen, Operations Manager, European KYC & KYB' },
      { id: 'account_owner', label: 'Account owner (Sales/Solutions)', placeholder: 'e.g., Lindsay Hatton' },
    ],
  },
] as const

export const CHECK_TYPE_OPTIONS = [
  { id: 'KYC', label: 'KYC', description: 'Individual identity verification' },
  { id: 'KYB', label: 'KYB', description: 'Business / UBO verification' },
  { id: 'AML', label: 'AML', description: 'PEP, sanctions, adverse media' },
  { id: 'KYT', label: 'KYT', description: 'Transaction monitoring (Sardine)' },
] as const

export const CONSENT_OPTIONS = [
  { id: 'unknown', label: 'Unknown', color: 'text-gray-400' },
  { id: 'has_consent', label: 'Consent confirmed', color: 'text-green-400' },
  { id: 'no_consent', label: 'No consent', color: 'text-red-400' },
  { id: 'partial', label: 'Partial consent', color: 'text-amber-400' },
] as const

export const MATCH_ELEMENTS = [
  { id: 'name', label: 'Name' },
  { id: 'dob', label: 'Date of Birth' },
  { id: 'address', label: 'Address' },
  { id: 'id', label: 'Government ID' },
] as const

/** Derive a human-readable match type label from source selections */
export function deriveMatchLabel(source1: string[], source2: string[]): { label: string; type: string } {
  const s1 = source1.filter(Boolean)
  const s2 = source2.filter(Boolean)
  const hasS1 = s1.length > 0
  const hasS2 = s2.length > 0

  if (!hasS1 && !hasS2) return { label: '', type: '' }

  const fmt = (els: string[]) => els.map(e => MATCH_ELEMENTS.find(m => m.id === e)?.label || e).join(' + ')

  if (hasS1 && !hasS2) {
    return { label: `1+1 — ${fmt(s1)} from one source`, type: '1+1' }
  }
  if (hasS1 && hasS2) {
    return { label: `2+2 — ${fmt(s1)} (Source 1) + ${fmt(s2)} (Source 2)`, type: '2+2' }
  }
  return { label: fmt(s2), type: '' }
}

// Legacy — kept for backward compatibility in PocVendorPlanning / PocIntake
export const MATCH_TYPE_OPTIONS = [
  { id: 'one_plus_address', label: '1+ Address', description: '1x Name + 1x Address match' },
  { id: 'one_plus_dob', label: '1+ DOB', description: '1x Name + 1x DOB match' },
  { id: 'two_plus', label: '2+2', description: 'Two data points from two independent sources' },
  { id: 'two_plus_id', label: '2+2 + Gov ID', description: 'Two sources plus government ID verification' },
  { id: 'gov_id_only', label: 'Gov ID Only', description: 'Government ID verification only' },
] as const

export const MATCH_TYPE_FIELDS: Record<string, { pass_criteria: string; address_enabled: boolean }> = {
  one_plus_address: { pass_criteria: 'Name and Address', address_enabled: true },
  one_plus_dob:     { pass_criteria: 'Name and Date of Birth', address_enabled: false },
  two_plus:         { pass_criteria: 'Two data points from two independent sources', address_enabled: true },
  two_plus_id:      { pass_criteria: 'Two sources plus Government ID', address_enabled: true },
  gov_id_only:      { pass_criteria: 'Government ID verification', address_enabled: false },
}
export const MATCH_TYPE_FIELDS_DEFAULT = { pass_criteria: '', address_enabled: true }

// Legacy exports kept for backward compatibility in other components
export const FUZZINESS_OPTIONS = [
  { id: 'exact', label: 'Exact', description: 'No tolerance — must match character-for-character' },
  { id: 'low', label: 'Low', description: 'Minor typos tolerated (1 char difference)' },
  { id: 'medium', label: 'Medium', description: 'Moderate tolerance (misspellings, abbreviations)' },
  { id: 'high', label: 'High', description: 'High tolerance (phonetic matching, partial matches)' },
] as const

export const FUZZINESS_FIELDS = [
  { id: 'name', label: 'Name' },
  { id: 'dob', label: 'Date of Birth' },
  { id: 'address', label: 'Address' },
] as const

export const PASS_FAIL_ELEMENTS = [
  { id: 'full_name', label: 'Full Name' },
  { id: 'dob', label: 'Date of Birth' },
  { id: 'address', label: 'Address' },
  { id: 'id_number', label: 'ID / National ID Number' },
  { id: 'phone', label: 'Phone Number' },
  { id: 'email', label: 'Email' },
] as const

export const EXECUTION_METHODS = [
  { id: 'customer_api', label: 'Customer runs via API', description: 'Preferred — customer makes create+verify API calls directly. Avoids vendor data cleaning.' },
  { id: 'techops_batch', label: 'Tech Ops batch', description: 'Tech Ops executes batch via mass remediation script. Requires 10 business days notice.' },
  { id: 'direct_vendor', label: 'Direct to vendor', description: 'Send file directly to GDC/Datazoo. Risk: vendors may clean data, inflating pass rates.' },
  { id: 'mixed', label: 'Mixed approach', description: 'Different methods for different vendors.' },
] as const

export const JIRA_TICKET_TYPES = [
  { id: 'account_setup', label: 'Account Setup', description: 'Child account, workflow, check_type/recipe' },
  { id: 'connector_config', label: 'Connector Config', description: 'Live connector setup (~10 business days)' },
  { id: 'batch_execution', label: 'Batch Execution', description: 'Run batch file via mass remediation script' },
  { id: 'socure_root_config', label: 'Socure Root Config', description: 'Country address rules — separate ticket to Alvin' },
  { id: 'other', label: 'Other', description: '' },
] as const

export const ADDRESS_MATCH_FIELDS = [
  { id: 'include_street', label: 'Street', default: true },
  { id: 'include_city', label: 'City', default: false },
  { id: 'include_state', label: 'State/Province', default: false },
  { id: 'include_zip', label: 'Postal/Zip Code', default: true },
] as const

// ==================== Socure Partner Backtest Intake Form ====================

export const SOCURE_PRODUCTS = [
  { id: 'kyc', label: 'KYC' },
  { id: 'docv', label: 'DocV' },
  { id: 'sigma_device', label: 'Sigma Device' },
  { id: 'sigma_identity', label: 'Sigma Identity' },
  { id: 'email_risk', label: 'EmailRisk' },
  { id: 'phone_risk', label: 'PhoneRisk' },
  { id: 'address_risk', label: 'Address Risk' },
  { id: 'global_watchlist', label: 'Global Watchlist' },
] as const

export const SOCURE_FUNNEL_POSITIONS = [
  { id: 'top_of_funnel', label: 'Top of Funnel (Initial applicant screening)' },
  { id: 'mid_funnel', label: 'Mid-Funnel (Pre-approval / additional checks)' },
  { id: 'step_up', label: 'Step-Up / Secondary Validation' },
  { id: 'other', label: 'Other (please specify)' },
] as const

export const SOCURE_PII_FIELDS = [
  { id: 'unique_id', label: 'Unique ID' },
  { id: 'submission_date', label: 'Submission Date' },
  { id: 'first_name', label: 'First Name' },
  { id: 'sur_name', label: 'Sur Name' },
  { id: 'email', label: 'Email' },
  { id: 'phone_number', label: 'Phone Number' },
  { id: 'date_of_birth', label: 'Date of Birth' },
  { id: 'national_id', label: 'National ID' },
  { id: 'address_line_1', label: 'Physical Address Line 1' },
  { id: 'address_line_2', label: 'Physical Address Line 2' },
  { id: 'city', label: 'City' },
  { id: 'state', label: 'State' },
  { id: 'zip', label: 'Zip' },
  { id: 'country', label: 'Country' },
] as const

export const SOCURE_OUTCOME_LABELS = [
  { id: 'kyc_approve', label: 'KYC Approve' },
  { id: 'kyc_reject', label: 'KYC Reject' },
  { id: 'confirmed_id_theft', label: 'Confirmed ID Theft' },
  { id: 'confirmed_first_party_fraud', label: 'Confirmed First Party Fraud' },
  { id: 'confirmed_synthetic_fraud', label: 'Confirmed Synthetic Identity Fraud' },
  { id: 'suspected_fraud', label: 'Suspected Fraud' },
  { id: 'no_fraud', label: 'No Fraud' },
  { id: 'no_labels', label: 'No Labels Provided' },
] as const
